import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const { FiCheck, FiX, FiArrowRight, FiActivity, FiVolume2, FiFileText, FiShield, FiInfo } = FiIcons;

/* Real ACX/Audible spec + include-skip rules, sourced from the AudiobookSmith
 * ACX Compliance Guide and the mastering chain in the integration decision brief. */
const specRows = [
  ['Loudness (RMS)', '-23 dB to -18 dB', 'Overall program level Audible measures on every file.'],
  ['Peak level', '-3 dB or lower', 'True-peak ceiling — no file may exceed it.'],
  ['Noise floor', '-60 dB RMS or lower', 'The silence between words must be genuinely quiet.'],
  ['Room tone', 'Head & tail', '0.5–1s of matched room tone at the start and end of every file.'],
  ['Format', 'MP3, 192 kbps CBR', '44.1 kHz, mono — the delivery format ACX expects.'],
  ['Per file', 'One file per chapter', 'Each chapter and each credit section as its own file.'],
];

const narrate = [
  'Opening credits (required, 30–60s)',
  'All numbered chapters',
  'Prologue, Epilogue, Foreword, Preface, Introduction (when narrative)',
  'Epigraphs — read into the chapter, not as a separate track',
  'Part titles — spoken as transitions between parts',
  'Closing credits (required, 10–30s)',
];

const skip = [
  'Copyright page (handled by the retailer)',
  'Table of contents',
  'Dedication & acknowledgments',
  'About the author',
  'Bibliography, index, glossary (unless invented terms)',
  'Praise / reviews / endorsements',
  'Footnotes, endnotes & next-book previews',
];

const AudiobookACXPage = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'ACX / Audible-Ready Audiobook Mastering — ManuscriptSmith',
    description: 'How ManuscriptSmith produces ACX/Audible-spec audiobook files: loudness, peak and noise-floor mastering, room tone, and automatic ACX include/skip rules.',
    url: 'https://manuscriptsmith.com/audiobook/acx'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="ACX / Audible-Ready Audiobook Files | ManuscriptSmith"
        description="Every chapter mastered to the exact ACX/Audible spec — -18 to -23 dB RMS, ≤ -3 dB peak, ≤ -60 dB noise floor, room tone, 192 kbps MP3 — with an automated compliance report and correct ACX include/skip handling."
        keywords="acx compliance, audible ready audiobook, acx mastering spec, audiobook loudness rms, audiobook noise floor, amazon virtual voice, acx include skip rules"
        canonicalUrl="https://manuscriptsmith.com/audiobook/acx"
        structuredData={structuredData}
      />

      <main>
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-3 py-1 rounded-full mb-5">
            Audiobook · ACX / Audible-ready
          </div>
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            Mastered to the spec{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">Audible actually checks.</span>
          </motion.h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            ACX rejections almost always come down to two things: audio that misses the loudness and noise-floor
            targets, and the wrong sections being narrated. We handle both automatically.
          </p>
        </section>

        {/* Spec table */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiActivity} className="w-7 h-7 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-900">The mastering spec, on every file</h2>
            </div>
            <p className="text-gray-600 mb-8">
              Each chapter runs through a mastering chain — high-pass filter, noise reduction, two-pass loudness
              normalisation, room tone, then a measured loudness/peak/floor gate — so it lands inside ACX tolerances
              before you ever upload.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-parchment-200">
              <table className="w-full text-left border-collapse min-w-[560px]">
                <thead>
                  <tr className="bg-parchment-100 text-gray-700 text-sm">
                    <th className="px-5 py-3 font-bold">Requirement</th>
                    <th className="px-5 py-3 font-bold">ACX / Audible target</th>
                    <th className="px-5 py-3 font-bold">Why it matters</th>
                  </tr>
                </thead>
                <tbody>
                  {specRows.map(([r, t, w], i) => (
                    <tr key={r} className={i % 2 ? 'bg-parchment-50' : 'bg-white'}>
                      <td className="px-5 py-4 font-semibold text-gray-900 align-top">{r}</td>
                      <td className="px-5 py-4 text-primary-700 font-mono text-sm align-top whitespace-nowrap">{t}</td>
                      <td className="px-5 py-4 text-gray-600 text-sm align-top">{w}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-4 flex items-start gap-2">
              <SafeIcon icon={FiFileText} className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Every production pass ships with an ACX compliance report — the measured RMS, peak, and floor for each file, so you can see it passed, not just hope it did.
            </p>
          </div>
        </section>

        {/* Include / skip */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 mb-3">
            <SafeIcon icon={FiVolume2} className="w-7 h-7 text-primary-600" />
            <h2 className="text-3xl font-bold text-gray-900">The right sections, narrated the right way</h2>
          </div>
          <p className="text-gray-600 mb-8 max-w-3xl">
            ACX has clear rules about what belongs in an audiobook. We apply them automatically, adjusting for
            fiction, non-fiction, children’s books, and poetry.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-parchment-200 p-7">
              <h3 className="flex items-center text-xl font-bold text-gray-900 mb-4">
                <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                </span>
                Always narrated
              </h3>
              <ul className="space-y-2.5">
                {narrate.map((t) => (
                  <li key={t} className="flex items-start text-gray-700">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-2.5 mt-0.5 flex-shrink-0" />{t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-parchment-200 p-7">
              <h3 className="flex items-center text-xl font-bold text-gray-900 mb-4">
                <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mr-3">
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </span>
                Automatically skipped
              </h3>
              <ul className="space-y-2.5">
                {skip.map((t) => (
                  <li key={t} className="flex items-start text-gray-700">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-rose-400 mr-2.5 mt-0.5 flex-shrink-0" />{t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Distribution honesty */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-parchment-100 border border-parchment-200 rounded-2xl p-8">
            <div className="flex items-start gap-3 mb-4">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">“ACX-ready” means the file, not a shortcut past the rules</h2>
            </div>
            <p className="text-gray-700 mb-4">
              Your files meet Audible’s technical bar. Getting an <em>AI-narrated</em> audiobook onto Audible itself
              goes through Amazon’s own <strong>Virtual Voice</strong> program on KDP — that’s the only sanctioned AI
              path, and we prep your manuscript and audio so it’s clean. For Spotify and wide distribution, your
              mastered files are ready for the pipelines those platforms accept. We’ll never tell you to upload AI
              audio somewhere that quietly forbids it.
            </p>
            <p className="text-sm text-gray-500 flex items-start gap-2">
              <SafeIcon icon={FiInfo} className="w-4 h-4 mt-0.5 flex-shrink-0" />
              From August 2026, EU rules require AI-generated audio to be disclosed. Our exports keep the provenance
              you need to comply.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">One book. Read and heard.</h2>
            <p className="text-lg opacity-90 mb-8">Finish your manuscript, then master it to ACX spec in the same place.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/audiobook/voices" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50 transition-all">
                Choose a voice <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25 transition-all">
                Get started
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AudiobookACXPage;
